/*
 * Wishlist storefront client.
 *
 * Guest:  ids live in localStorage under WISHLIST_KEY.
 * Logged in: ids live on the server; we call the App Proxy endpoint.
 * On login, we POST {op: "merge"} so a guest's local list moves onto the
 * customer's account.
 *
 * The proxy path is configured in shopify.app.toml / partner dashboard. By
 * default this script uses "/apps/wishlist" — override via:
 *   <script>window.WishlistConfig = { proxyPath: "/apps/oxygen/wishlist" }</script>
 */
(function () {
  // This asset is included by every wishlist block (button, count, page,
  // embed, toggle snippet). When two or more appear on the same page the
  // browser executes the script once per <script> tag, which previously
  // registered the click handler multiple times — so each click ran toggle()
  // an even number of times and cancelled itself out ("button does nothing").
  // Guard so initialisation happens exactly once per page.
  if (window.__pcmtWishlistInit) return;
  window.__pcmtWishlistInit = true;

  const PROXY_PATH =
    (window.WishlistConfig && window.WishlistConfig.proxyPath) ||
    "/apps/wishlist";
  const STORAGE_KEY = "sw_wishlist_v1";
  const MERGE_FLAG = "sw_wishlist_merged_v1";
  // Ids the shopper added while logged OUT, awaiting a one-time merge into their
  // account on login. We merge ONLY these — never the whole local cache — so a
  // stale cache on a second device can't resurrect items deleted elsewhere.
  const PENDING_KEY = "sw_wishlist_pending_v1";

  // Storage format: array of { id: string, handle: string|null }. The id drives
  // server sync (DB + alerts); the handle lets a theme page render cards fast
  // via the native /products/{handle}.js endpoint. Older builds stored a flat
  // array of ids — readItems() migrates those on read (handle becomes null).
  function readItems() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(arr)) return [];
      return arr
        .map((el) =>
          el && typeof el === "object"
            ? { id: String(el.id), handle: el.handle ? String(el.handle) : null }
            : { id: String(el), handle: null }
        )
        .filter((it) => it.id && it.id !== "undefined" && it.id !== "null");
    } catch (e) {
      return [];
    }
  }

  function writeItems(items) {
    try {
      const seen = new Set();
      const deduped = [];
      for (const it of items) {
        const id = String(it.id);
        if (!id || seen.has(id)) continue;
        seen.add(id);
        deduped.push({ id, handle: it.handle ? String(it.handle) : null });
      }
      localStorage.setItem(STORAGE_KEY, JSON.stringify(deduped));
      // Let a wishlist page / count on the same page re-render without a reload.
      window.dispatchEvent(new CustomEvent("sw:wishlist:updated"));
    } catch (e) {
      // localStorage may be unavailable (private mode / quota) — ignore.
    }
  }
function readPending() {
    try {
      const raw = localStorage.getItem(PENDING_KEY);
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.map(String).filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  function writePending(ids) {
    try {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify(Array.from(new Set(ids.map(String))))
      );
    } catch (e) {}
  }
  function readIds() {
    return readItems().map((it) => it.id);
  }

  // id -> handle for the ids we currently know a handle for.
  function handleMap() {
    const m = {};
    for (const it of readItems()) if (it.handle) m[it.id] = it.handle;
    return m;
  }
 function isLoggedIn() {
    // Authoritative signal first: the theme renders window.WishlistConfig.loggedIn
    // from Liquid's {{ customer }} (inline script before this file loads). This is
    // the ONLY reliable check under Shopify's NEW customer accounts, where none of
    // the classic client-side signals below exist on storefront pages.
    if (window.WishlistConfig && typeof window.WishlistConfig.loggedIn === "boolean") {
      return window.WishlistConfig.loggedIn;
    }
    // Fallback for classic themes/accounts.
    return Boolean(
      (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) ||
        document.body.classList.contains("customer-logged-in")
    );
  }

  // Never let a slow/hanging proxy (e.g. a DB pool timeout) keep a request
  // pending forever — abort after `ms` so the UI degrades gracefully.
  function fetchWithTimeout(url, opts, ms) {
    opts = opts || {};
    if (typeof AbortController === "undefined") return fetch(url, opts);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms || 6000);
    const merged = {};
    for (const k in opts) merged[k] = opts[k];
    merged.signal = ctrl.signal;
    return fetch(url, merged).finally(() => clearTimeout(t));
  }

  async function serverList() {
    try {
      const res = await fetchWithTimeout(PROXY_PATH, { credentials: "same-origin" }, 6000);
      if (!res.ok) return { items: [], guest: true };
      return await res.json();
    } catch (e) {
      return { items: [], guest: true };
    }
  }

  async function serverMutate(op, payload) {
    let res;
    try {
      res = await fetchWithTimeout(PROXY_PATH, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ op, ...payload }),
      }, 8000);
    } catch (e) {
      return { ok: false };
    }
    return res.json().catch(() => ({ ok: false }));
  }

  async function mergeOnLogin() {
    if (!isLoggedIn()) return;
    if (sessionStorage.getItem(MERGE_FLAG) === "1") return;
    // Merge ONLY the guest buffer (items added while logged out) — never the
    // full local cache. Pushing the whole cache up re-adds items the shopper
    // removed on another device: a fresh session on a second device still holds
    // them in localStorage, so the old "merge everything" logic resurrected
    // every deletion. list() runs right after this and makes the server list
    // authoritative (which already includes any freshly merged guest picks).
    const pending = readPending();
    if (pending.length === 0) {
      sessionStorage.setItem(MERGE_FLAG, "1");
      return;
    }
    const result = await serverMutate("merge", { productIds: pending });
    if (result && result.ok) {
      writePending([]); // guest buffer consumed
      sessionStorage.setItem(MERGE_FLAG, "1");
    }
  }

  function idTail(gid) {
    if (!gid) return gid;
    const i = gid.lastIndexOf("/");
    return i >= 0 ? gid.slice(i + 1) : gid;
  }

  async function list() {
    if (isLoggedIn()) {
      const r = await serverList();
      // Only let the server REPLACE the local list when it authoritatively
      // knows the customer (guest:false). If the proxy failed, timed out, or
      // couldn't identify the customer (password gate, not really signed in),
      // it returns guest:true / empty — in that case KEEP the local list and
      // never wipe it to the server's empty response.
      if (!r || r.guest || r.ok === false) return readIds();
      const known = handleMap();
      const items = (r.items || []).map((i) => {
        const id = idTail(i.productId);
        return { id, handle: known[id] || null };
      });
      writeItems(items);
      return items.map((i) => i.id);
    }
    return readIds();
  }

  async function add(productId, handle) {
    const id = String(productId);
    const items = readItems();
    const existing = items.find((it) => it.id === id);
    if (!existing) {
      items.push({ id, handle: handle ? String(handle) : null });
    } else if (handle && !existing.handle) {
      existing.handle = String(handle); // backfill a handle we just learned
    }
  writeItems(items);
    if (isLoggedIn()) {
      await serverMutate("add", { productId: id });
    } else {
      // Guest add — remember it as a genuine guest pick to merge on login.
      const pend = readPending();
      if (!pend.includes(id)) writePending(pend.concat(id));
    }
    paint();
  }

  async function remove(productId) {
    const id = String(productId);
    writeItems(readItems().filter((it) => it.id !== id));
    // Drop from the guest buffer too, so an add-then-remove before login
    // doesn't get merged back in.
    writePending(readPending().filter((p) => p !== id));
    if (isLoggedIn()) {
      await serverMutate("remove", { productId: id });
    }
    paint();
  }

  async function toggle(productId, handle) {
    const id = String(productId);
    if (readItems().some((it) => it.id === id)) {
      await remove(id);
    } else {
      await add(id, handle);
    }
  }

  // Remove every item ("remove all" on the wishlist page). Clears local storage
  // always, and the server-side list too for logged-in shoppers.
  async function clear() {
    writeItems([]);
    if (isLoggedIn()) {
      await serverMutate("clear", {});
    }
    paint();
  }

  function paint() {
    const ids = readIds();
    document.querySelectorAll("[data-wishlist-btn]").forEach((el) => {
      const pid = el.getAttribute("data-product-id");
      const on = ids.includes(pid);
      el.setAttribute("aria-pressed", on ? "true" : "false");
      el.classList.toggle("is-active", on);
      const label = el.querySelector("[data-wishlist-label]");
      if (label) label.textContent = on ? "In wishlist" : "Add to wishlist";
    });
    // Keep EVERY counter on the page in sync (header, wishlist page, …).
    document.querySelectorAll("[data-wishlist-count]").forEach(applyCount);
  }

  // Set one counter element to the current wishlist count + badge visibility.
  function applyCount(el) {
    const n = readIds().length;
    const want = String(n);
    const wantHidden = n === 0;
    if (el.textContent !== want) el.textContent = want;
    if (el.hidden !== wantHidden) el.hidden = wantHidden;
    const ariaWant = wantHidden ? "true" : "false";
    if (el.getAttribute("aria-hidden") !== ariaWant) {
      el.setAttribute("aria-hidden", ariaWant);
    }
  }

  // Some themes reuse a shared badge (e.g. .sw-cart-badge) and their cart
  // script keeps rewriting its text/visibility with the CART count. Guard each
  // wishlist counter so it always reflects the wishlist, even if another script
  // fights it. Re-writing only when the value is wrong prevents an update loop.
  function guardCounters() {
    if (typeof MutationObserver === "undefined") return;
    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      if (el.__wlGuarded) return;
      el.__wlGuarded = true;
      const obs = new MutationObserver(() => applyCount(el));
      obs.observe(el, {
        childList: true,
        characterData: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["hidden", "aria-hidden", "style", "class"],
      });
    });
  }

  function bind() {
    document.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-wishlist-btn]");
      if (!btn) return;
      e.preventDefault();
      const pid = btn.getAttribute("data-product-id");
      // Optional: buttons that also carry the handle enable fast native-render
      // wishlist pages. Falls back to id-only (server hydrate) when absent.
      const handle = btn.getAttribute("data-product-handle");
      if (pid) toggle(pid, handle);
    });
    // Keep count/heart state in sync when another component mutates the list.
    window.addEventListener("sw:wishlist:updated", paint);
  }

  function init() {
    bind();
    // Paint instantly from localStorage — no network, so hearts/counters are
    // correct immediately and nothing here can delay the page.
    paint();
    guardCounters();
    if (!isLoggedIn()) return;
    // Defer the server sync until after the page has loaded so a slow proxy
    // can never keep the browser in a "waiting…" / loading state.
    const sync = () => {
      mergeOnLogin()
        .then(list)
        .then(paint)
        .catch(() => {});
    };
    if (document.readyState === "complete") {
      sync();
    } else {
      window.addEventListener("load", sync, { once: true });
    }
  }

  // Fetch product details (title/image/price/url) for the current wishlist.
  // Works for both logged-in (server hydrates) and guest (server hydrates by id list).
  async function loadFull() {
    try {
      if (isLoggedIn()) {
        const res = await fetchWithTimeout(
          PROXY_PATH + "?hydrate=1",
          { credentials: "same-origin" },
          8000
        );
        if (!res.ok) return [];
        const data = await res.json();
        return data.products || [];
      }
      const ids = readIds();
      if (ids.length === 0) return [];
      const res = await fetchWithTimeout(
        PROXY_PATH,
        {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ op: "hydrate", productIds: ids }),
        },
        8000
      );
      if (!res.ok) return [];
      const data = await res.json();
      return data.products || [];
    } catch (e) {
      return [];
    }
  }

  // Subscribe / unsubscribe from a back-in-stock or price-drop alert on a
  // product. `contact` allows guests to pass {email, phone}; logged-in
  // shoppers just pass {}.
  async function subscribeAlert(productId, kind, contact, priceThreshold) {
    const res = await fetch(PROXY_PATH, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "subscribe",
        productId,
        kind,
        email: contact && contact.email,
        phone: contact && contact.phone,
        priceThreshold,
      }),
    });
    return res.json().catch(() => ({ ok: false }));
  }

  async function unsubscribeAlert(productId, kind, contact) {
    const res = await fetch(PROXY_PATH, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        op: "unsubscribe",
        productId,
        kind,
        email: contact && contact.email,
        phone: contact && contact.phone,
      }),
    });
    return res.json().catch(() => ({ ok: false }));
  }

  // Public API for advanced themes / custom pages.
  window.Wishlist = {
    add,
    remove,
    toggle,
    clear,
    list,
    items: readItems, // [{ id, handle }] — for handle-based page renderers
    loadFull,
    subscribeAlert,
    unsubscribeAlert,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-paint for headers/counters that render (or re-render) after init:
  // sticky headers, drawer headers, section-rendered headers, bfcache restores.
  window.addEventListener("load", paint);
  window.addEventListener("pageshow", paint);
  document.addEventListener("shopify:section:load", paint);

  // Robust catch: repaint whenever a wishlist counter/button is injected into
  // the DOM later (sticky header on scroll, cart/nav drawer, AJAX section swap).
  if (typeof MutationObserver !== "undefined") {
    const SEL = "[data-wishlist-count],[data-wishlist-btn]";
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const n of m.addedNodes) {
          if (
            n.nodeType === 1 &&
            ((n.matches && n.matches(SEL)) ||
              (n.querySelector && n.querySelector(SEL)))
          ) {
            paint();
            guardCounters(); // guard any newly-injected counters too
            return;
          }
        }
      }
    });
    obs.observe(document.body, { childList: true, subtree: true });
  }
})();